import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { IconButton } from "./Button";
import { CloseIcon } from "@/components/icons";

/**
 * A focus-trapping dialog.
 *
 * Built on the native `<dialog>` element rather than a div with `role`:
 * `showModal()` brings the top layer, the backdrop, inert content behind it
 * and Escape-to-close for free, and those are exactly the parts a hand-rolled
 * overlay gets wrong. The backdrop is themed through the token custom
 * properties like everything else.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  footer?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialog}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog element itself.
        if (event.target === dialog.current) onClose();
      }}
      aria-label={title}
      className={cn(
        "bg-card text-ink rounded-container shadow-popover border-border m-auto border p-0",
        "backdrop:bg-ink/40 w-[min(92vw,var(--modal-width))] max-h-[86vh] overflow-hidden",
      )}
      style={{ ["--modal-width" as string]: wide ? "62rem" : "34rem" }}
    >
      <div className="flex max-h-[86vh] flex-col">
        <header className="border-border flex items-start gap-3 border-b px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="type-h4 text-ink">{title}</h2>
            {description ? (
              <p className="type-support text-ink-secondary mt-1">{description}</p>
            ) : null}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <CloseIcon className="size-4" />
          </IconButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <footer className="border-border bg-surface flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </dialog>
  );
}
